import { ChangeDetectorRef, Component, computed, inject, input, model, OnInit, output, signal } from '@angular/core';
import {
    ConfigInstanceToken,
    isDefined,
    MaterialModel,
    MaterialService,
    MaterialUtilitiesService,
    ModalClass,
} from '@evorait/pace-core';
import { faChevronDown, faChevronUp, faSync } from '@fortawesome/free-solid-svg-icons';
import {
    BOOTSTOCK_RETURN_ACTION,
    getBootstockReturnAction,
} from '../../../../core/services/bootstock-return/bootstock-return.interface';
import { MaterialReturnP58Component } from '../../../../material/material-return-p58/material-return-p58.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { take } from 'rxjs';
import MaterialReturnBootstockModel from '../../../../material/material-return-bootstock/material-return-bootstock.model';
import { SmartlockerService } from '../../../../smartlocker/services/smartlocker.service';
import { SmartlockerData } from '../../../../smartlocker/interfaces/smartlocker-doors.interface';
import { SmartlockerPickupComponent } from '../../../../smartlocker/components/smartlocker-pickup/smartlocker-pickup.component';
import { LogInstanceToken } from '@evorait/pace-logging';
import { MaterialStockService } from '../../../../material/services/material-stock-service';

@Component({
    selector: 'pace-confirmation-material-stock-details-expandable',
    templateUrl: './confirmation-material-stock-details-expandable.component.html',
    styleUrls: ['./confirmation-material-stock-details-expandable.component.scss'],
    standalone: false,
})
export class ConfirmationMaterialStockDetailsExpandableComponent implements OnInit {
    public material = model.required<MaterialModel>();
    public bootStock = input<MaterialModel[]>();
    public isModelNotification = input.required<boolean>();
    public isBootStock = input<boolean>(false);
    public showBootStockActions = input<boolean>(false);
    public isLoadingBootStockActions = input<boolean>(false);
    public bootstockReturnIds = input<Map<string, boolean>>(new Map<string, boolean>());
    public smartlockers = input<SmartlockerData[]>([]);
    public bootstockReturnSuccess = output<void>();
    public smartlockerPickup = output<void>();
    public switchToBootStock = output<void>();
    public sortField = output<string>();

    private readonly isMaterialInSmartlocker = computed<boolean>(() =>
        this.smartlockerService.isSmartlockerMaterial(this.material().storageLocationDesc.value),
    );

    public smartlockerData = computed<SmartlockerData | undefined>(() => {
        if (this.isBootStock() || !this.isMaterialInSmartlocker()) {
            return undefined;
        }
        const smartlockerId = this.smartlockerService.getSmartlockerIdFromStorageLocationDesc(
            this.material().storageLocationDesc.value,
        );
        return this.smartlockers()?.find((sl) => sl.SmartlockerId === smartlockerId);
    });

    private readonly modalService = inject(BsModalService);
    private readonly materialService = inject(MaterialService);
    private readonly configService = inject(ConfigInstanceToken);
    private readonly materialUtilities = inject(MaterialUtilitiesService);
    private readonly smartlockerService = inject(SmartlockerService);
    private readonly log = inject(LogInstanceToken);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly materialStockService = inject(MaterialStockService);

    public readonly BOOTSTOCK_RETURN_ACTION = BOOTSTOCK_RETURN_ACTION;
    public faChevronDown = faChevronDown;
    public faChevronUp = faChevronUp;
    public readonly faLoading = faSync;
    public isCollapsed = true;
    public orderField = signal<{ oldValue: string; newValue: string }>({ oldValue: '', newValue: '' });
    public isReverse = computed<boolean>(() => {
        if (this.orderField().oldValue.length > 0 && this.orderField().newValue.length > 0) {
            return this.orderField().oldValue === this.orderField().newValue;
        }
        return false;
    });
    public showBootstockActionColumn = computed<boolean>(
        () =>
            this.isBootStock() &&
            this.showBootStockActions() &&
            (this.isLoadingBootStockActions() ||
                this.bootstockReturnIds()?.size > 0 ||
                this.material().isBootStockScrapable),
    );

    public bootstockReturnAction = computed<BOOTSTOCK_RETURN_ACTION>(() => {
        if (!this.isBootStock() || !this.showBootStockActions()) {
            return BOOTSTOCK_RETURN_ACTION.NONE;
        }

        return getBootstockReturnAction(this.material(), this.bootstockReturnIds());
    });

    public ngOnInit(): void {
        if (!this.isBootStock() && this.isMaterialInSmartlocker()) {
            this.material().quantity.options = {
                ...this.material().quantity.options,
                readonly: true,
            };
        }
    }

    public openBootstockReturnDialog(material: MaterialModel, action: BOOTSTOCK_RETURN_ACTION): void {
        // Clone the material to avoid mutating the original stock-list instance.
        // This prevents state leakage when the user cancels/closes the modal.
        const clonedMaterial = material.clone();

        const cseDopList = this.materialService.getCSEDopList();
        const frontId = this.configService.getUserFrontId();
        const modalRef = this.modalService.show(MaterialReturnP58Component, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new MaterialReturnBootstockModel(
                    clonedMaterial,
                    cseDopList,
                    this.materialUtilities,
                    frontId,
                    action === BOOTSTOCK_RETURN_ACTION.RETURN,
                ),
                bootstockMaterial: clonedMaterial,
                isBootstockReturn: action === BOOTSTOCK_RETURN_ACTION.RETURN,
                isBootstockScrap: action === BOOTSTOCK_RETURN_ACTION.SCRAP,
            },
            ariaLabelledBy: 'material-return-p58-modal-title',
        });

        modalRef.content?.bootstockSubmitSuccess$.pipe(take(1)).subscribe(() => {
            this.bootstockReturnSuccess.emit();
        });
    }

    public openSmartlockerPickupModal(): void {
        if (!isDefined(this.smartlockerData())) {
            this.log.error(
                `Error in ConfirmationMaterialStockDetailsExpandableComponent -> openSmartlockerPickupModal -> no smartlocker data available so no pickup should be possible`,
            );
            return;
        }

        this.materialStockService
            .checkIfMaterialExistsInBootStock([this.material()], this.bootStock() ?? [])
            .subscribe((continuePickup) => {
                if (!continuePickup) {
                    this.switchToBootStock.emit();
                    return;
                }
                const modalInstance = this.modalService.show(SmartlockerPickupComponent, {
                    backdrop: 'static',
                    keyboard: false,
                    class: ModalClass.MODAL_MEDIUM,
                    initialState: {
                        material: this.material,
                        smartlockerData: this.smartlockerData(),
                    },
                    ariaLabelledBy: 'smartlocker-pickup-title',
                });

                modalInstance.content?.pickedInSmartlocker.subscribe(() => {
                    this.changeDetectorRef.markForCheck();
                    this.changeDetectorRef.detectChanges();
                    this.smartlockerPickup.emit();
                });
            });
    }
}
